import {
  faTriangleExclamation,
  faShieldBlank,
  faStethoscope,
  faUserGroup,
  faPaw,
  faMugSaucer,
  faBell,
  faLocationArrow,
  faLocationDot,
  faRoad,
} from "@fortawesome/free-solid-svg-icons"
import { SafetyPlanResponseOptionType } from "./constants"
import {
  Enum_Safety_Plan_Section_Icons_Enum,
  Enum_Safety_Plan_Type_Enum,
} from "../generated/graphql"

export type CustomValue = boolean | string | Place | Contact

export interface TextBoxOptionProps {
  option: ResponseOption
  type: string
  onChange: (id: string, value: string[], type: string) => void
  /** When true, renders an "Add" button that appends additional input rows (practitioner view only). */
  allowMultiple?: boolean
}

export interface AllResponseOption {
  display_order: number
  id: string
  header: string
  help_text: string
  description: string
  allOptionsMap: Map<string, SelectedOption>
  icon: Enum_Safety_Plan_Section_Icons_Enum
  icon_color: string
  subtitle: string
}
export interface SelectedOption {
  id: string
  custom_values: [CustomValue] | string[]
  category_type: string
  display_order: number
  response_text: string
}

export interface SelectedSection {
  id: string
  description: string
  display_order: number
  header: string
  help_text: string
  selectedOptionsMap: Map<string, SelectedOption>
  icon: Enum_Safety_Plan_Section_Icons_Enum
  icon_color: string
  subtitle: string
}

export interface ResponseOption {
  display_order: number
  id: string
  response_text: string
  isSelected: boolean
}

export interface Option {
  category: string
  categoryDescription: string
  category_type: string
  category_id: string // category id
  response_options: ResponseOption[]
}

export interface Section {
  id: string
  description: string
  display_order: number
  header: string
  help_text: string
  icon: Enum_Safety_Plan_Section_Icons_Enum
  icon_color: string
  subtitle: string
  optionsMap: Map<string, Option>
}

export interface UpdateSafetyPlanOption {
  id: string
  value: [CustomValue] | string[]
  category_type: string
}
export interface Place {
  place: string
}

export interface Contact {
  first: string
  last: string
  phone: string
}

export interface Selected {
  id: string
  value: [CustomValue] | string[]
  category_type: string
}

export interface ConfirmSafetyPlan {
  id: string
  custom_values: [CustomValue] | string[]
}

/** Raw shape of a checklist entry as stored in responses.checklist JSON. */
export interface ChecklistEntry {
  category_id: string
  selected_option_ids: string[]
  completed_option_ids: string[]
}

/** A single checklist option resolved against the template. */
export interface ChecklistResponseItem {
  id: string
  response_text: string
  category_id: string
  category_type: string
  display_order: number
  completed: boolean
}

/** A checklist entry after its option IDs have been resolved to display text. */
export interface HydratedChecklistEntry {
  category_id: string
  items: ChecklistResponseItem[]
}

export interface SafetyPlanOptionsProps {
  option: SelectedOption
}

export interface Category {
  category: string
  categoryDescription: string
  category_id: string
  category_type: string
  response_options: ResponseOption[]
}

export interface Custom {
  id: string
  value: string[]
  category_type: string
}

export interface BaseSafetyPlanProps {
  option: ResponseOption | ResponseOption[]
  onClick: (id: string, value: any, type: string) => void
  type: string
  selection: Selected[]
}

export interface SafetyPlanProps extends BaseSafetyPlanProps {
  onChange: (id: string, value: string[], type: string) => void
  /** When true, TEXT_BOX options render an "Add" button for multiple entries (practitioner view only). */
  allowMultiple?: boolean
  newSafetyPlanView?: boolean
}

export interface OptionProps {
  option: ResponseOption
  type: string
  selection: Selected[]
  onClick: (id: string, value: any, type: string) => void
}

export interface SafetyPlanCategoriesProps {
  category: Category
  onClick: (id: string, value: any, type: string) => void
  selection: Selected[]
  onChange: (id: string, value: string[], type: string) => void
}

/**
 * Props for the shared Categories component used by both
 * EditSafetyPlan and SafetyPlanPractitioner.
 *
 * @param category     - The category group to render, including its type and response options.
 * @param onClick      - Callback invoked when the user selects or toggles an option.
 * @param selection    - The current list of selected items.
 * @param onChange     - Callback invoked when free-text content changes.
 * @param skipIfPlace  - When true, renders nothing for the "Add a place" category
 *                       (used by EditSafetyPlan, which moves place entry to the dialog footer).
 */
export interface SharedCategoriesProps {
  category: Option
  onClick: (id: string, value: any, type: string) => void
  selection: Selected[]
  onChange: (id: string, value: string[], type: string) => void
  skipIfPlace?: boolean
  newSafetyPlanView?: boolean
}

export interface SafetyPlanSectionsPractitionerProps {
  options: Map<string, Section>
  onClick: (id: string, value: any, type: string) => void
  selection: Selected[]
  onChange: (id: string, value: string[], type: string) => void
  openSection: (id: string) => void
  collapsed: string[]
  onSave: () => void
  showSave: boolean
}

export interface SafetyPlanSectionPractitionerProps {
  selected: Section
  options: Map<string, Section>
  selection: Selected[]
  onChange: (id: string, value: string[], type: string) => void
  onClick: (id: string, value: any, type: string) => void
  openSection: (id: string) => void
  collapsed: string[]
}

export interface SafetyPlanCategoriesPractitionerProps {
  category: Option
  selection: Selected[]
  onChange: (id: string, value: string[], type: string) => void
  onClick: (id: string, value: any, type: string) => void
}

export interface SafetyPlanOptionsPractitionerProps {
  type: string
  props: ResponseOption | ResponseOption[]
  selection: Selected[]
  onChange: (id: string, value: string[], type: string) => void
  onClick: (id: string, value: any, type: string) => void
}

export interface SafetyPlanPractitionerRadioOptionProps {
  props: ResponseOption
  type: string
  selection: Selected[]
  onClick: (id: string, value: any, type: string) => void
}

/**
 * Builds the ordered array of categories to render in the edit modal, along
 * with derived boolean flags used to conditionally show UI elements.
 *
 * - Moves the TEXT_BOX category to the front so custom text input appears first.
 * - Aggregates TEXT_BOX and PLACE response options into a synthetic "USER ADDED"
 *   category appended at the end, used to display user-created entries.
 *
 * @param category - The section data for the currently selected safety plan item.
 * @returns The ordered categories array, the place category (if present), and
 *          booleans indicating whether contact, place, and radio options exist.
 */
export function buildCategoriesArray(category: Section | undefined): {
  categoriesArr: Category[]
  placeCategory: Category | undefined
  foundContact: boolean
  foundPlace: boolean
  foundRadio: boolean
} {
  const categoriesArr: Category[] = category
    ? [...category.optionsMap.values()]
    : []

  const foundIdx = categoriesArr.findIndex(
    (el) => el.category_type === SafetyPlanResponseOptionType.TEXT_BOX
  )
  const foundPlaceIndx = categoriesArr.findIndex(
    (el) => el.category_type === SafetyPlanResponseOptionType.PLACE
  )

  const foundContact = categoriesArr.some(
    (el) => el.category_type === SafetyPlanResponseOptionType.CONTACT
  )
  const foundPlace = categoriesArr.some(
    (el) => el.category_type === SafetyPlanResponseOptionType.PLACE
  )
  const foundRadio = categoriesArr.some(
    (el) => el.category_type === SafetyPlanResponseOptionType.RADIO
  )

  const customText = categoriesArr[foundIdx]
  const placeCategory = categoriesArr[foundPlaceIndx]

  const userAddedCategory: any = {
    category: "USER ADDED",
    category_id: "user_added_category_id",
    category_type: "user_added",
    response_options: [] as ResponseOption[],
  }

  if (customText) {
    const opts: ResponseOption[] = customText.response_options
    userAddedCategory["response_options"].push([...opts])
  }
  if (placeCategory) {
    const placeOpts = placeCategory.response_options
    userAddedCategory["response_options"].push([...placeOpts])
  }
  if (customText || placeCategory) categoriesArr.push(userAddedCategory)

  if (foundIdx !== -1) {
    const customEle = categoriesArr[foundIdx]
    categoriesArr.splice(foundIdx, 1)
    categoriesArr.unshift(customEle)
  }

  return { categoriesArr, placeCategory, foundContact, foundPlace, foundRadio }
}

/**
 * Returns a new `selectedItems` array reflecting the result of toggling the
 * given option.
 *
 * - Contact / Place types accumulate values (multiple entries allowed).
 * - All other types toggle: selecting an already-selected item deselects it.
 * - Unselected items are appended as new entries.
 *
 * @param selectedItems - The current array of selected options.
 * @param id - The id of the option being toggled.
 * @param value - The value(s) associated with the option.
 * @param type - The category type of the option (e.g. "contact", "place", "radio").
 * @returns A new array with the toggle applied — the original array is never mutated.
 */
export function updateSelectedItems(
  selectedItems: Selected[],
  id: string,
  value: Selected["value"],
  type: string
): Selected[] {
  const found = selectedItems.findIndex((el) => el.id === id)
  const isContactOrPlace = [
    SafetyPlanResponseOptionType.CONTACT,
    SafetyPlanResponseOptionType.PLACE,
  ].includes(type as SafetyPlanResponseOptionType)

  if (found === -1) {
    return [...selectedItems, { id, value, category_type: type }]
  }

  if (isContactOrPlace) {
    return selectedItems.map((item, idx) =>
      idx === found
        ? { ...item, value: [...item.value, ...value] as [CustomValue] }
        : item
    )
  }

  return selectedItems.filter((el) => el.id !== id)
}

/**
 * Merges a custom text-box entry into the selected items array without mutating
 * the original.
 *
 * - If an entry with the same id already exists, its values are appended to.
 * - If no matching entry exists, the custom entry is appended as a new item.
 *
 * @param selectedItems - The current array of selected options.
 * @param custom - The custom text-box entry to merge in.
 * @returns A new array with the custom entry merged — the original is never mutated.
 */
export function mergeCustomEntry(
  selectedItems: Selected[],
  custom: Custom
): Selected[] {
  const normalizedValues = (custom.value ?? [])
    .map((v) => v.trim())
    .filter((v) => v.length > 0)

  // Don't add/append anything for whitespace-only input.
  if (normalizedValues.length === 0) return [...selectedItems]

  const existingIdx = selectedItems.findIndex((el) => el.id === custom.id)
  if (existingIdx > -1) {
    return selectedItems.map((item, idx) =>
      idx === existingIdx
        ? {
            ...item,
            value: [...item.value, ...normalizedValues] as [CustomValue],
          }
        : item
    )
  }
  return [
    ...selectedItems,
    {
      id: custom.id,
      value: normalizedValues,
      category_type: custom.category_type,
    },
  ]
}

/**
 * Rebuilds the section-keyed selected options map from a flat list of updated
 * option values.
 *
 * Uses a Map lookup (O(1) per option) and spreads `optValue` rather than mutating it in place.
 *
 * @param allOptions - The full template map of all available sections and their options.
 * @param data - The flat list of updated option selections from the edit modal.
 * @returns A new section-keyed Map of selected options with updated custom values.
 */
export function buildSelectedOptionsMap(
  allOptions: Map<string, AllResponseOption>,
  data: UpdateSafetyPlanOption[]
): Map<string, SelectedSection> {
  const dataById = new Map(data.map((dt) => [dt.id, dt]))
  const result = new Map<string, SelectedSection>()

  for (const option of allOptions.values()) {
    const {
      id,
      description,
      display_order,
      header,
      help_text,
      icon,
      icon_color,
      subtitle,
    } = option
    const selectedOptionsMap = new Map<string, SelectedOption>()

    for (const optValue of option.allOptionsMap.values()) {
      const match = dataById.get(optValue.id)
      if (match) {
        selectedOptionsMap.set(optValue.id, {
          ...optValue,
          custom_values: match.value,
        })
      }
    }

    result.set(id, {
      id,
      description,
      display_order,
      header,
      help_text,
      selectedOptionsMap,
      icon,
      icon_color,
      subtitle,
    })
  }

  return result
}

export function planTypeToString(
  planType: Enum_Safety_Plan_Type_Enum | undefined,
  isTitle: boolean
) {
  switch (planType) {
    case Enum_Safety_Plan_Type_Enum.RelapsePrevention:
      return isTitle ? "Relapse Prevention Plan" : "relapse prevention plan"
    case Enum_Safety_Plan_Type_Enum.Safety:
      return isTitle ? "Safety Plan" : "safety plan"
    default:
      return isTitle ? "Plan" : "plan"
  }
}

export function keyToIconDef(key: Enum_Safety_Plan_Section_Icons_Enum) {
  switch (key) {
    case Enum_Safety_Plan_Section_Icons_Enum.MugSaucer:
      return faMugSaucer
    case Enum_Safety_Plan_Section_Icons_Enum.Paw:
      return faPaw
    case Enum_Safety_Plan_Section_Icons_Enum.Shield:
      return faShieldBlank
    case Enum_Safety_Plan_Section_Icons_Enum.Stethoscope:
      return faStethoscope
    case Enum_Safety_Plan_Section_Icons_Enum.TriangleExclamation:
      return faTriangleExclamation
    case Enum_Safety_Plan_Section_Icons_Enum.UserGroup:
      return faUserGroup
    case Enum_Safety_Plan_Section_Icons_Enum.Bell:
      return faBell
    case Enum_Safety_Plan_Section_Icons_Enum.LocationArrow:
      return faLocationArrow
    case Enum_Safety_Plan_Section_Icons_Enum.LocationDot:
      return faLocationDot
    case Enum_Safety_Plan_Section_Icons_Enum.Tracks:
      return faRoad
    default:
      return faTriangleExclamation
  }
}
